// src/pages/PasswordChange.js
import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { changePassword, fetchUserById, clearSelectedUser } from '../redux/actions/userActions';
import { useNavigate } from 'react-router-dom';
import GlobalModal from './components/modals/globalModal';

function PasswordChange() {
  // Retrieve the selected user from Redux
  const selectedUser = useSelector((state) => state.user.selectedUser);
  // Also retrieve the auth user for self-change checking
  const authUser = useSelector((state) => state.auth.user);

  // All hooks are called unconditionally:
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  // Derive userId exclusively from selected user
  const userId = selectedUser ? selectedUser.id : null;

  // Determine if this is a self password change
  const isSelfChange = selectedUser && authUser && selectedUser.id === authUser.id;

  // Modal configuration state for both success and error
  const [modalConfig, setModalConfig] = useState({
    show: false,
    type: '', // "error" or "success"
    message: '',
  });

  // Fetch user data on mount if userId exists
  useEffect(() => {
    if (userId && (authUser.role === 'admin' || authUser.role === 'superUser')) {
      dispatch(fetchUserById(userId));
    }
  }, [authUser.role, dispatch, userId]);

  // Clear selected user when leaving this page
  useEffect(() => {
    return () => {
      dispatch(clearSelectedUser());
    };
  }, [dispatch]);

  // If no selected user, block the component from rendering the form.
  if (!selectedUser) {

    setTimeout(() => {
      navigate('/');
    }
    , 3000);
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          No user selected. Please select a user to change the password.
        </Alert>
      </Container>
    );
  }

    // Function to hide the modal
  const handleModalClose = () => {
    setModalConfig((prev) => ({ ...prev, show: false }));
    if (modalConfig.type === 'success') {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  // Helpers to set modal configuration
  const handleApiFailure = (errorMessage) => {
    setModalConfig({ show: true, type: 'error', message: errorMessage });
  };

  const handleApiSuccess = (successMessage) => {
    setModalConfig({ show: true, type: 'success', message: successMessage });
  };

  const isMyself = () => {
    if(selectedUser.id === authUser.id) {
      return 'Change Password for my account (' + authUser.email + ')';
    }
    return 'Change Password for ' + selectedUser.email;
  }

  const handleSubmit = (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match. Please try again.');
      return;
    }
    if (isSelfChange && !currentPassword) {
      setError('Current password is required.');
      return;
    }
    setError('');
    // Dispatch changePassword with currentPassword only if self-changing.
    dispatch(changePassword(userId, { currentPassword: isSelfChange ? currentPassword : undefined, newPassword }))
      .then(() => {
        handleApiSuccess('Password changed successfully.');
        // Optionally, add success handling here.
        // dispatch(clearSelectedUser());
        // navigate(-1);
      })
      .catch((err) => {
        handleApiFailure('Error changing password. Please try again.');
        console.error('Error changing password:', err);
        setError('Error changing password. Please try again.');
      });
  };

  return (
    <Container className="mt-4">
      <Card>
        <Card.Body>
          <Card.Title>{isMyself()}</Card.Title>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form onSubmit={handleSubmit}>
            {isSelfChange && (
              <Form.Group controlId="currentPassword" className="mb-2">
                <Form.Label>Current Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Enter current password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </Form.Group>
            )}
            <Form.Group controlId="newPassword" className="mb-2">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group controlId="confirmPassword" className="mb-2">
              <Form.Label>Confirm New Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </Form.Group>
            <Button variant="primary" type="submit">
              Change Password
            </Button>
          </Form>
        </Card.Body>
      </Card>

      {/* Global Modal for Success and Error */}
      <GlobalModal
        show={modalConfig.show}
        onHide={handleModalClose}
        type={modalConfig.type}
        message={modalConfig.message}
      />
    </Container>
  );
}

export default PasswordChange;
